import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Clock, Loader2 } from 'lucide-react';
import { Footer } from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ConfirmationPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const paymentStatus = searchParams.get('status');
  
  const [status, setStatus] = useState('checking');
  const [booking, setBooking] = useState(null);
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    // Handle "pay_later" flow
    if (bookingId && paymentStatus === 'pending') {
      fetchBookingDetails(bookingId);
      return;
    }
    
    // Handle Stripe payment flow
    if (sessionId) {
      pollPaymentStatus();
    }
  }, [sessionId, bookingId, paymentStatus]);

  const fetchBookingDetails = async (id) => {
    try {
      const response = await axios.get(`${API}/bookings/${id}`);
      setBooking(response.data);
      setStatus('pending_payment');
    } catch (error) {
      console.error('Error fetching booking:', error);
      setStatus('error');
    }
  };

  const pollPaymentStatus = async () => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('paid');
        const bookingResponse = await axios.get(`${API}/bookings`);
        const latestBooking = bookingResponse.data.find(b => b.payment_status === 'paid');
        setBooking(latestBooking);
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setAttempts(attempts + 1);
      setTimeout(pollPaymentStatus, pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  const formatIDR = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen" data-testid="confirmation-page">
      <div className="py-24 md:py-32 px-6 md:px-12 min-h-screen flex items-center justify-center">
        <div className="max-w-3xl mx-auto w-full">
          {status === 'checking' && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="text-4xl md:text-5xl font-heading font-light mb-4">
                Verifying Payment
              </h1>
              <p className="text-lg text-foreground-muted">
                Please wait while we verify your payment...
              </p>
            </motion.div>
          )}

          {/* Pay Later - Pending Payment Status */}
          {status === 'pending_payment' && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
              data-testid="payment-pending"
            >
              <Clock className="w-20 h-20 text-amber-500 mx-auto mb-8" />
              <h1 className="text-5xl md:text-7xl tracking-tighter leading-[0.9] font-medium font-heading mb-6">
                Booking <span className="text-amber-500">Pending</span>
              </h1>
              
              <div
                className="p-8 md:p-12 mb-8"
                style={{
                  background: 'rgba(20, 20, 22, 0.7)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid rgba(234, 224, 213, 0.1)'
                }}
              >
                <div className="bg-amber-500/10 border border-amber-500/30 p-6 mb-8">
                  <p className="text-lg text-amber-200 mb-2">
                    <strong>Next step</strong>
                  </p>
                  <p className="text-foreground-muted">
                    A team member will contact you within <strong>24 hours</strong> to 
                    arrange payment via bank transfer (BCA/Mandiri) or cash at the studio.
                  </p>
                </div>

                {booking && (
                  <div className="text-left space-y-4 border-t border-border pt-8">
                    <h3 className="text-xl font-heading font-light mb-4">Booking Details</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-foreground-muted">Name</p>
                        <p className="text-foreground">{booking.customer_name}</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted">Contact</p>
                        <p className="text-foreground">{booking.customer_phone}</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted">Date</p>
                        <p className="text-foreground">{booking.date}</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted">Time</p>
                        <p className="text-foreground">{booking.start_time}</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted">Duration</p>
                        <p className="text-foreground">{booking.duration_hours} hours</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted">Amount Due</p>
                        <p className="text-foreground font-semibold text-primary">{formatIDR(booking.total_price)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-base text-foreground-muted mb-8">
                You will receive a confirmation email once payment is completed.
              </p>

              <a
                href="/"
                className="inline-block px-8 py-4 bg-primary text-primary-foreground font-body font-semibold tracking-wide hover:scale-[0.98] transition-transform duration-200"
                data-testid="back-home-btn"
              >
                Back to Home
              </a>
            </motion.div>
          )}

          {status === 'paid' && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
              data-testid="payment-success"
            >
              <CheckCircle className="w-20 h-20 text-primary mx-auto mb-8" />
              <h1 className="text-5xl md:text-7xl tracking-tighter leading-[0.9] font-medium font-heading mb-6">
                Your Session Is <span className="text-primary">Confirmed</span>
              </h1>
              
              <div
                className="p-8 md:p-12 mb-8"
                style={{
                  background: 'rgba(20, 20, 22, 0.7)',
                  backdropFilter: 'blur(24px)',
                  border: '1px solid rgba(234, 224, 213, 0.1)'
                }}
              >
                <p className="text-lg md:text-xl leading-relaxed tracking-wide font-light text-foreground mb-8">
                  You will receive preparation guidelines soon.
                  <br />
                  Our team will contact you to finalize the technical details.
                </p>

                {booking && (
                  <div className="text-left space-y-4 border-t border-border pt-8">
                    <h3 className="text-xl font-heading font-light mb-4">Booking Details</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-foreground-muted">Date</p>
                        <p className="text-foreground">{booking.date}</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted">Time</p>
                        <p className="text-foreground">{booking.start_time}</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted">Duration</p>
                        <p className="text-foreground">{booking.duration_hours} hours</p>
                      </div>
                      <div>
                        <p className="text-foreground-muted">Total Paid</p>
                        <p className="text-foreground font-semibold">{formatIDR(booking.total_price)}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <p className="text-base text-foreground-muted mb-8">
                We look forward to welcoming you.
              </p>

              <a
                href="/"
                className="inline-block px-8 py-4 bg-primary text-primary-foreground font-body font-semibold tracking-wide hover:scale-[0.98] transition-transform duration-200"
                data-testid="back-home-btn"
              >
                Back to Home
              </a>
            </motion.div>
          )}

          {(status === 'error' || status === 'expired' || status === 'timeout') && (
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl font-heading font-light mb-4">
                Payment Issue
              </h1>
              <p className="text-lg text-foreground-muted mb-8">
                {status === 'expired' && 'Your payment session has expired.'}
                {status === 'timeout' && 'Payment verification took too long. Please check your email.'}
                {status === 'error' && 'An error occurred while verifying the payment.'}
              </p>
              <a
                href="/booking"
                className="inline-block px-8 py-4 bg-primary text-primary-foreground font-body font-semibold tracking-wide hover:scale-[0.98] transition-transform duration-200"
              >
                Try Again
              </a>
            </motion.div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}